import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  HealthReadingData,
  ParameterHistoryParams,
} from 'services/face-scan/type';
import PreviousScans from '../PreviousScans';
import {
  CategorizedReadingData,
  ReadingDataWithSubParameters,
} from '../../../../types';
import { Button } from 'components/ui/button';
import VitalSignDetail from './VitalSignDetail';

interface MobileScanReportProps {
  readings: HealthReadingData[];
  setReadings: Dispatch<SetStateAction<HealthReadingData[]>>;
  onScan: () => void;
  scanData: ReadingDataWithSubParameters[];
  data: CategorizedReadingData;
  historyParams: ParameterHistoryParams;
  refetchScanReports?: () => void;
  scrollViewElement: React.ReactNode;
  setHistoryParams: Dispatch<SetStateAction<ParameterHistoryParams>>;
  isParameterFetching?: boolean;
}

export default function MobileScanReport({
  readings,
  setReadings,
  onScan,
  scanData,
  data,
  historyParams,
  refetchScanReports,
  scrollViewElement,
  setHistoryParams,
  isParameterFetching,
}: MobileScanReportProps) {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const scanId = searchParams.get('scan_id');

  const [selectedReadingId, setSelectedReadingId] = useState<string>('');

  const onDelete = () => {
    refetchScanReports?.();
    setSelectedReadingId('');
  };

  useEffect(() => {
    if (scanId && scanData && scanData.length) {
      setSelectedReadingId(scanId);
    }
  }, [scanId, scanData]);

  if (selectedReadingId) {
    return (
      <VitalSignDetail
        data={data}
        scanData={scanData}
        historyParams={historyParams}
        setSelectedReadingId={setSelectedReadingId}
        readingId={selectedReadingId}
        refetchScanReports={onDelete}
        setHistoryParams={setHistoryParams}
        isParameterFetching={isParameterFetching}
      />
    );
  }

  return (
    <div className='relative block p-4 sm:hidden'>
      <div className='flex justify-end'>
        <Button
          className='h-8 text-sm rounded-full bg-rose-400 hover:bg-rose-400/90'
          size='sm'
          onClick={onScan}
        >
          <Camera className='w-4 h-4 mr-2' />
          {t('start_scan')}
        </Button>
      </div>

      <div className='mt-4'>
        <PreviousScans
          readings={readings}
          setReadings={setReadings}
          selectedReadingId={selectedReadingId}
          setSelectedReadingId={setSelectedReadingId}
          scrollViewElement={scrollViewElement}
        />
      </div>
    </div>
  );
}
